import { css } from '@emotion/react';
import { useFeatureFlags, useScanEditingContextSlice } from '@kaiizen/kai-pro-api-react-state';
import { getTotalCostIfDefined } from '@kaiizen/kai-scan-template-system';

import { formatUsd } from '../../../Presenters';
import { useProjectTypes } from '../../../State';
import { COLORS2, LAYOUT2, TYPOGRAPHY2 } from '../../../Styles';
import { RoomMeasurementsPencilIcon } from '../../../Widgets/StyledIcons';
import { ScanNavigatorItem } from './ScanNavigatorItem';

const SCAN_OVERVIEW_CONTENT_STYLE = css`
  label: SCAN_OVERVIEW_CONTENT_STYLE;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SCAN_OVERVIEW_TITLE_STYLE = css`
  label: SCAN_OVERVIEW_TITLE_STYLE;
  ${TYPOGRAPHY2.xSmallBold};
  color: ${COLORS2.namedColors.gray};
`;

const SCAN_OVERVIEW_PRICE_STYLE = css`
  label: SCAN_OVERVIEW_PRICE_STYLE;
  ${TYPOGRAPHY2.xLarge};
  color: ${COLORS2.alwaysDark.text.green};
`;

const SCAN_OVERVIEW_INFO_STYLE = css`
  label: SCAN_OVERVIEW_INFO_STYLE;
  ${TYPOGRAPHY2.xSmall};
  color: ${COLORS2.alwaysDark.text.lightGray};
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SCAN_OVERVIEW_INFO_LABEL_STYLE = css`
  label: SCAN_OVERVIEW_INFO_LABEL_STYLE;
  ${TYPOGRAPHY2.xSmall};
  color: ${COLORS2.namedColors.gray};
`;

const SCAN_OVERVIEW_INFO_VALUE_STYLE = css`
  label: SCAN_OVERVIEW_INFO_VALUE_STYLE;
  ${TYPOGRAPHY2.xSmallBold};
  color: ${COLORS2.alwaysDark.text.white};
  text-transform: uppercase;
`;

const STRIKE_OUT_OLD_COST_STYLE = css`
  label: STRIKE_OUT_OLD_COST_STYLE;
  text-decoration: line-through;
  color: ${COLORS2.alwaysDark.text.darkGray};
`;

const SCAN_OVERVIEW_SQFT_ROW_STYLE = css`
  label: SCAN_OVERVIEW_SQFT_ROW_STYLE;
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacingHalf}px;
`;

const SCAN_OVERVIEW_MEASURED_SQFT_STYLE = css`
  label: SCAN_OVERVIEW_MEASURED_SQFT_STYLE;
  ${TYPOGRAPHY2.large};
  color: ${COLORS2.alwaysDark.text.white};
`;

const SCAN_OVERVIEW_LISTED_SQFT_STYLE = css`
  label: SCAN_OVERVIEW_LISTED_SQFT_STYLE;
  ${TYPOGRAPHY2.small};
  color: ${COLORS2.namedColors.gray};
`;

export const ScanNavigatorOverviewItem = ({
  isSelected,
  onSelect,
}: {
  isSelected: boolean;
  onSelect: () => void;
}) => {
  const { featureFlags: { useRoomMeasurements } } = useFeatureFlags();
  const { baselineScanRoot, scanRoot, scan, property, project } = useScanEditingContextSlice(['baselineScanRoot', 'scanRoot', 'scan', 'property', 'project']);
  const { getProjectTypeLabel } = useProjectTypes();
  const baselineTotalCost = getTotalCostIfDefined(baselineScanRoot);
  const totalCost = getTotalCostIfDefined(scanRoot);

  const totalMeasuredSqft = scanRoot?.groups?.reduce((total, group) => {
    return total + (group.optimized.reducedValues.roomArea ?? 0);
  }, 0) ?? 0;

  if (!scan) return null;

  if (useRoomMeasurements) {
    return (
      <ScanNavigatorItem
        isSelected={isSelected}
        onSelect={onSelect}
        showBox={true}
      >
        <div css={SCAN_OVERVIEW_CONTENT_STYLE}>
          <div css={SCAN_OVERVIEW_INFO_STYLE}>
            <div css={SCAN_OVERVIEW_INFO_VALUE_STYLE}>
              {getProjectTypeLabel(project?.customerMetadata?.projectType) ?? '—'} PROJECT
            </div>
            <div css={SCAN_OVERVIEW_PRICE_STYLE}>
              {baselineTotalCost && baselineTotalCost !== totalCost && (
                <div css={STRIKE_OUT_OLD_COST_STYLE}>
                  {formatUsd(baselineTotalCost, true)}
                </div>
              )}
              {formatUsd(totalCost, true)}
            </div>
            <div css={SCAN_OVERVIEW_SQFT_ROW_STYLE}>
              <div css={SCAN_OVERVIEW_MEASURED_SQFT_STYLE}>
                {totalMeasuredSqft > 0 ? Math.round(totalMeasuredSqft).toLocaleString() : '-'} sqft{' '}
                <span css={SCAN_OVERVIEW_LISTED_SQFT_STYLE}>({property?.customerMetadata?.squareFeet?.toLocaleString() ?? '-'} listed)</span>
              </div>
              <RoomMeasurementsPencilIcon />
            </div>
          </div>
        </div>
      </ScanNavigatorItem>
    );
  }

  return (
    <ScanNavigatorItem
      isSelected={isSelected}
      onSelect={onSelect}
      showBox={true}
    >
      <div css={SCAN_OVERVIEW_CONTENT_STYLE}>
        <div css={SCAN_OVERVIEW_INFO_STYLE}>
          <div css={SCAN_OVERVIEW_TITLE_STYLE}>PROJECT OVERVIEW</div>
          <div css={SCAN_OVERVIEW_PRICE_STYLE}>
            {baselineTotalCost && baselineTotalCost !== totalCost && (
              <div css={STRIKE_OUT_OLD_COST_STYLE}>
                {formatUsd(baselineTotalCost)}
              </div>
            )}
            {formatUsd(totalCost)}
          </div>
        </div>
        <div css={SCAN_OVERVIEW_INFO_VALUE_STYLE}>
          {getProjectTypeLabel(project?.customerMetadata?.projectType) ?? '—'}
        </div>
        <div css={SCAN_OVERVIEW_INFO_STYLE}>
          <div css={SCAN_OVERVIEW_INFO_LABEL_STYLE}>
            Listed sqft: <span css={SCAN_OVERVIEW_INFO_VALUE_STYLE}>{property?.customerMetadata?.squareFeet?.toLocaleString() ?? '-'}</span>
          </div>
          <div css={SCAN_OVERVIEW_INFO_LABEL_STYLE}>
            Measured sqft: <span css={SCAN_OVERVIEW_INFO_VALUE_STYLE}>{totalMeasuredSqft > 0 ? totalMeasuredSqft?.toLocaleString() : '-'}</span>
          </div>
        </div>
      </div>
    </ScanNavigatorItem>
  );
};
